import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { DraftInvoiceRequest } from '@/api/handlers/resources/purchases/requests';
import type { GetDraftInvoiceResponse } from '@/api/handlers/resources/purchases/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import {
    invalidateDraftInvoiceCaches,
    upsertDraftInvoiceCaches,
} from './draftInvoiceCache';

/**
 * First save of a manual-import draft (`POST /purchases/draft-invoices`). On
 * success the saved draft is written straight into the drafts-list and detail
 * caches — the new row is visible on /purchases immediately — and the list +
 * summary are invalidated so the server fills in what the response can't
 * carry (supplier display name, computed total). The query function never
 * rejects — the form branches on `result.success` and surfaces the server's
 * 400 `error` text in a banner.
 */
export const useCreateDraftInvoiceMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetDraftInvoiceResponse>,
        Error,
        DraftInvoiceRequest
    >({
        mutationFn: (body) => apiClient.purchases.createDraftInvoice(body),
        onSuccess: (result) => {
            if (result.success) {
                upsertDraftInvoiceCaches(queryClient, result.response);
                invalidateDraftInvoiceCaches(queryClient);
            }
        },
    });
};
